import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { SEO } from "../components/SEO";
import axios from "axios";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Globe, Map, Bot, Plane, Hotel, Car, Train, BookOpen, Smartphone,
  ChevronRight, GripVertical, Settings2, Check, Sparkles,
} from "lucide-react";

const ALL_FEATURES = [
  { id: "countries", icon: Globe, label: "Country & Visa", description: "Visa info & travel tips", path: "/countries", iconClass: "icon-orange", glowClass: "glow-border-amber", photo: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&q=80" },
  { id: "roadtrip", icon: Map, label: "Route & Sights", description: "Plan your roadtrip", path: "/roadtrip", iconClass: "icon-cyan", glowClass: "glow-border-cyan", photo: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&q=80" },
  { id: "ai-planner", icon: Bot, label: "AI Planner", description: "Personalized trip planning", path: "/ai-planner", iconClass: "icon-purple", glowClass: "glow-border-purple", badge: "AI", photo: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&q=80" },
  { id: "journal", icon: BookOpen, label: "Travel Journal", description: "Document your memories", path: "/journal", iconClass: "icon-amber", glowClass: "glow-border-amber", photo: "https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=400&q=80" },
  { id: "flights", icon: Plane, label: "Flights", description: "Search & compare flights", path: "/flights", iconClass: "icon-amber", glowClass: "glow-border-amber", photo: "https://images.unsplash.com/photo-1622818622723-b6a6dd81e918?w=400&q=80" },
  { id: "hotels", icon: Hotel, label: "Hotels", description: "Find places to stay", path: "/hotels", iconClass: "icon-rose", glowClass: "glow-border-rose", photo: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
  { id: "bus-train", icon: Train, label: "Bus & Train", description: "Ground transportation", path: "/bus-train", iconClass: "icon-teal", glowClass: "glow-border-emerald", photo: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=400&q=80" },
  { id: "esim", icon: Smartphone, label: "Travel eSIM", description: "Stay connected worldwide", path: "/esim", iconClass: "icon-cyan", glowClass: "glow-border-cyan", badge: "New", photo: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=400&q=80" },
  { id: "car-rental", icon: Car, label: "Car Rental", description: "Rent a car anywhere", path: "/car-rental", iconClass: "icon-blue", glowClass: "glow-border-cyan", photo: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=400&q=80" },
];

const DEFAULT_ORDER = ALL_FEATURES.map((f) => f.id);

const SortableCard = ({ feature, editMode, onNavigate, index }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: feature.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : "auto",
  };
  const Icon = feature.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`animate-fade-in-up stagger-${Math.min(index + 1, 9)}`}
      data-testid={`feature-row-${feature.id}`}
    >
      {editMode ? (
        <div
          {...attributes}
          {...listeners}
          className="flex items-center gap-3 p-3.5 glass-card border-dashed cursor-grab active:cursor-grabbing touch-none select-none"
          style={{ borderStyle: 'dashed', borderColor: 'rgba(255,255,255,0.12)' }}
          data-testid={`drag-handle-${feature.id}`}
        >
          <GripVertical className="w-5 h-5 text-slate-600 flex-shrink-0" />
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${feature.iconClass}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-white">{feature.label}</h3>
            <p className="text-xs text-slate-500">{feature.description}</p>
          </div>
        </div>
      ) : (
        <button
          onClick={() => onNavigate(feature.path)}
          className={`w-full relative overflow-hidden rounded-2xl glass-card-interactive ${feature.glowClass}`}
          data-testid={`feature-${feature.label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {/* Background photo */}
          <div className="absolute inset-0">
            <img src={feature.photo} alt="" className="w-full h-full object-cover opacity-40" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a]/70 via-[#0f172a]/50 to-[#0f172a]/30" />
          </div>
          <div className="relative flex items-center gap-4 p-4 lg:p-5">
            <div className={`w-12 h-12 lg:w-14 lg:h-14 rounded-xl flex items-center justify-center flex-shrink-0 feature-icon-animated ${feature.iconClass}`}>
              <Icon className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
            </div>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <h3 className="text-[15px] lg:text-lg font-semibold text-white">{feature.label}</h3>
                {feature.badge && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                    feature.badge === "New"
                      ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/20"
                      : "bg-purple-500/15 text-purple-400 border border-purple-500/20"
                  }`}>
                    {feature.badge === "AI" && <Sparkles className="w-2.5 h-2.5 inline mr-0.5 -mt-0.5" />}
                    {feature.badge}
                  </span>
                )}
              </div>
              <p className="text-xs lg:text-sm text-slate-400 mt-0.5">{feature.description}</p>
            </div>
            <ChevronRight className="w-4 h-4 lg:w-5 lg:h-5 text-slate-500 flex-shrink-0" />
          </div>
        </button>
      )}
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  useEffect(() => { loadOrder(); }, []);

  const loadOrder = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/dashboard-order`, { withCredentials: true });
      if (data.order?.length) {
        const merged = [...data.order.filter((id) => ALL_FEATURES.some((f) => f.id === id))];
        ALL_FEATURES.forEach((f) => { if (!merged.includes(f.id)) merged.push(f.id); });
        setOrder(merged);
      }
    } catch { /* non-critical: order load */ }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setOrder((prev) => {
        const oldIdx = prev.indexOf(active.id);
        const newIdx = prev.indexOf(over.id);
        return arrayMove(prev, oldIdx, newIdx);
      });
    }
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/dashboard-order`, { order }, { withCredentials: true });
      setEditMode(false);
    } catch { /* non-critical: order save */ }
    setSaving(false);
  };

  const featureMap = Object.fromEntries(ALL_FEATURES.map((f) => [f.id, f]));
  const sortedFeatures = order.map((id) => featureMap[id]).filter(Boolean);

  return (
    <div className="page-container" data-testid="dashboard">
      <SEO title="Dashboard" description="Your travel command center." path="/dashboard" />

      {/* Ambient background orbs */}
      <div className="ambient-orb ambient-orb-cyan w-72 h-72 -top-20 -right-20" style={{ animation: 'orbFloat 8s ease-in-out infinite' }} />
      <div className="ambient-orb ambient-orb-purple w-56 h-56 top-1/2 -left-20" style={{ animation: 'orbFloat 10s ease-in-out infinite reverse' }} />

      <div className="page-content p-4 pt-6 pb-28">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 animate-fade-in-up">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-white">
              Where to next?
            </h1>
          </div>
          <button
            onClick={() => editMode ? saveOrder() : setEditMode(true)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium transition-all duration-300 ${
              editMode
                ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 shadow-lg shadow-emerald-500/10"
                : "bg-white/[0.04] text-slate-400 border border-white/[0.08] hover:bg-white/[0.08] hover:text-white"
            }`}
            data-testid="edit-order-btn"
          >
            {editMode ? (saving ? "Saving..." : <><Check className="w-3.5 h-3.5" /> Done</>) : <><Settings2 className="w-3.5 h-3.5" /> Reorder</>}
          </button>
        </div>

        {/* Feature grid */}
        <div className="max-w-2xl lg:max-w-5xl mx-auto" data-testid="feature-grid">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={order} strategy={verticalListSortingStrategy} disabled={!editMode}>
              <div className="flex flex-col lg:grid lg:grid-cols-2 gap-3">
                {sortedFeatures.map((feature, index) => (
                  <SortableCard key={feature.id} feature={feature} editMode={editMode} onNavigate={navigate} index={index} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
